/**
 * Free census of the client-side error stream (TES-2004).
 *
 * WHY THIS EXISTS
 *
 * The org's Sentry error quota has been exhausted since 2026-08-24 and ingestion
 * is frozen until the 2026-09-15 reset, so client errors are counted but never
 * stored — they cannot be read in the Sentry UI. ~306 browser envelopes a day
 * still reach the `/monitoring` tunnel and are refused with a 429, and there is
 * no way to name them from Sentry itself.
 *
 * So the browser reports a one-line summary of each *distinct* error to a
 * first-party endpoint, which writes it to the Vercel runtime log. Vercel logs
 * cost nothing and draw down no quota, which is the whole point: this names the
 * residual stream without spending the resource we are trying to conserve. It
 * is the client-side twin of the `csp-report forward` census in lib/csp-report.ts.
 *
 * TWO PROPERTIES THIS MODULE MUST KEEP
 *
 * 1. It never reports its own failures anywhere. Reporting a telemetry-transport
 *    failure through a telemetry pipe is the exact feedback loop that made this
 *    incident self-sustaining: `/api/csp-report` used to `captureException` when
 *    its forward to Sentry failed, so once the quota was spent every CSP report
 *    cost two billed events and the second one said "the quota is spent". A
 *    census that could itself raise an error would reintroduce that shape.
 *    `sendBeacon` is fire-and-forget by construction and the call is wrapped.
 *
 * 2. Its volume is bounded and sub-linear in the error volume. One visitor in a
 *    broken session can emit thousands of events — Sentry recorded 19,207
 *    `client_discarded` on 2026-08-24 — and a beacon per occurrence would just
 *    move the flood to a different pipe. Each distinct fingerprint is reported
 *    on power-of-two occurrences only (1st, 2nd, 4th, 8th, ...), so a
 *    10,000-occurrence loop costs 14 beacons and still reports its true
 *    magnitude via `n`. Distinct fingerprints are capped per page session too.
 *
 * The census counts what the app *generated*, not what Sentry received: it runs
 * in `beforeSend`, which is upstream of the SDK's own transport-level rate-limit
 * backoff. That is deliberate — the discarded volume is what determines whether
 * the stream fits in the quota once ingestion resumes.
 */

export const CLIENT_ERROR_CENSUS_ENDPOINT = '/api/client-error-census'

/** Enough to identify a defect; short enough that no free-form text runs long. */
const MAX_VALUE_LENGTH = 100
const MAX_FRAME_LENGTH = 60
const MAX_PATH_LENGTH = 80

/**
 * Distinct fingerprints reported per page session. A page that trips more than
 * this many *different* defects is already a total failure, and the first dozen
 * name it.
 */
export const MAX_DISTINCT_FINGERPRINTS = 12

type CensusFrame = {
  filename?: string | null
  abs_path?: string | null
}

type CensusException = {
  type?: string | null
  value?: string | null
  stacktrace?: {
    frames?: CensusFrame[] | null
  } | null
}

export type CensusEventLike = {
  message?: string | null
  logentry?: {
    message?: string | null
    formatted?: string | null
  } | null
  exception?: {
    values?: CensusException[] | null
  } | null
  tags?: Record<string, unknown> | null
}

export type ClientErrorCensus = {
  /** Exception type (`RangeError`), or `message` for a captureMessage event. */
  t: string
  /** Truncated, query-stripped exception value — this is what names the defect. */
  v: string
  /** Basename of the frame that threw. Empty when there is no usable stack. */
  f: string
  /** First-party pathname the error fired on. Never a query string. */
  p: string
  /** Occurrence count for this fingerprint in this page session. */
  n: number
  /**
   * The catalog row an audio failure names, from the event's `sound_id` tag.
   * Empty for every other event.
   *
   * A playback failure's whole diagnostic value is *which file* broke, and the
   * message is identical across rows — without this the census collapses every
   * broken asset into one entry and answers nothing (TES-1874). Sentry's own tag
   * index would do this, but the error quota is exhausted until 2026-10-15, so
   * the census is the only place the ids can be read before then.
   */
  s: string
}

/**
 * A row id, and nothing that could pass for one of the other fields. Sound ids
 * are UUIDs; the class is kept slightly wider so a non-UUID key would still be
 * legible rather than silently dropped.
 */
const SOUND_ID_PATTERN = /^[A-Za-z0-9_-]{1,40}$/

/**
 * Strips the query and fragment off any URL in free-form text.
 *
 * Exception messages routinely embed the URL that failed, and on this site those
 * carry ids and search terms. The origin and path are what identify the defect;
 * everything after `?` is visitor input we have no reason to log.
 */
export function redactCensusText(raw: string): string {
  return raw
    .replace(/(https?:\/\/[^\s?#]*)[?#]\S*/g, '$1')
    .trim()
    .slice(0, MAX_VALUE_LENGTH)
}

function basenameOf(rawUrl: string): string {
  const withoutQuery = rawUrl.split(/[?#]/, 1)[0]
  const segments = withoutQuery.split('/')
  return (segments[segments.length - 1] || withoutQuery).slice(0, MAX_FRAME_LENGTH)
}

/**
 * The frame that actually threw.
 *
 * Sentry orders `frames` oldest-first, so the throwing frame is the last entry,
 * not the first. Reading `frames[0]` would report the page's entry chunk for
 * every error and make the whole column useless.
 */
function throwingFrameBasename(exception: CensusException | undefined): string {
  const frames = exception?.stacktrace?.frames ?? []
  for (let index = frames.length - 1; index >= 0; index -= 1) {
    const url = frames[index]?.filename ?? frames[index]?.abs_path ?? ''
    if (url) return basenameOf(url)
  }
  return ''
}

function firstText(...candidates: Array<string | null | undefined>): string {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) return candidate.trim()
  }
  return ''
}

export function buildClientErrorCensus(
  event: CensusEventLike,
  pathname: string,
  occurrence: number,
): ClientErrorCensus {
  const exception = event.exception?.values?.[0] ?? undefined
  const soundId = event.tags?.sound_id

  return {
    t: redactCensusText(firstText(exception?.type, 'message')),
    v: redactCensusText(
      firstText(exception?.value, event.message, event.logentry?.formatted, event.logentry?.message),
    ),
    f: throwingFrameBasename(exception),
    p: pathname.split(/[?#]/, 1)[0].slice(0, MAX_PATH_LENGTH),
    n: occurrence,
    s: typeof soundId === 'string' && SOUND_ID_PATTERN.test(soundId) ? soundId : '',
  }
}

/**
 * A fingerprint that ignores `n` and the path, so one defect is one entry — but
 * keeps `s`, because for an audio failure the row *is* the defect and two broken
 * files are two things to fix.
 */
export function censusFingerprint(census: ClientErrorCensus): string {
  return `${census.t}|${census.v}|${census.f}|${census.s}`
}

/**
 * Powers of two only: 1, 2, 4, 8, 16, ...
 *
 * Keeps beacon volume logarithmic in occurrence count while still reporting the
 * true magnitude — see the volume note in the module header.
 */
export function isReportableOccurrence(occurrence: number): boolean {
  return occurrence > 0 && (occurrence & (occurrence - 1)) === 0
}

/**
 * Per-page-session occurrence counts, module-scoped on purpose.
 *
 * A per-component or per-instance counter would report the same defect once per
 * mount — the mistake `lib/audio-playback-telemetry.ts` documents for `/sounds`,
 * where a player is mounted per card and a per-instance ref reported one shared
 * broken asset once per card.
 */
const occurrencesByFingerprint = new Map<string, number>()

/** Test seam — production code never calls this. */
export function resetClientErrorCensusForTests(): void {
  occurrencesByFingerprint.clear()
}

/**
 * Decides whether this event should produce a beacon, and returns the payload.
 *
 * Split out from the transport so the whole policy is unit-testable without a
 * DOM. Returns null when the event is a repeat that falls between reporting
 * points, or when the page session has already named its fingerprint budget.
 */
export function nextCensusPayload(
  event: CensusEventLike,
  pathname: string,
): ClientErrorCensus | null {
  const provisional = buildClientErrorCensus(event, pathname, 1)
  const fingerprint = censusFingerprint(provisional)
  const seen = occurrencesByFingerprint.get(fingerprint)

  if (seen === undefined && occurrencesByFingerprint.size >= MAX_DISTINCT_FINGERPRINTS) {
    return null
  }

  const occurrence = (seen ?? 0) + 1
  occurrencesByFingerprint.set(fingerprint, occurrence)

  if (!isReportableOccurrence(occurrence)) return null

  return { ...provisional, n: occurrence }
}

/* ------------------------------------------------------------------------- *
 * Wire contract — the server half.
 *
 * Both sides of the beacon live in this module so the shape can only ever be
 * changed in one place. The route re-validates and re-clamps everything rather
 * than trusting `buildClientErrorCensus` to have run: the endpoint is public,
 * so the browser is not the only thing that can post to it.
 * ------------------------------------------------------------------------- */

/**
 * Strips everything that could forge or break a log line.
 *
 * A newline in a census field would let a caller inject what looks like a
 * separate runtime-log entry, and the census is read by grepping those lines.
 */
function sanitizeLogField(raw: unknown, maxLength: number): string {
  if (typeof raw !== 'string') return ''
  // eslint-disable-next-line no-control-regex
  return raw.replace(/[\u0000-\u001f\u007f]+/g, ' ').trim().slice(0, maxLength)
}

/** For the two fields that are a bare path, never a sentence. */
function stripQueryAndFragment(raw: string): string {
  return raw.split(/[?#]/, 1)[0]
}

export function parseClientErrorCensus(rawBody: string): ClientErrorCensus | null {
  let parsed: unknown
  try {
    parsed = JSON.parse(rawBody)
  } catch {
    return null
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null
  const record = parsed as Record<string, unknown>

  // Redaction is re-applied here, not just trusted from the browser. The client
  // half strips query strings before sending, but this endpoint is public, so a
  // body that never went through `buildClientErrorCensus` must not be able to
  // put a query string into the log either. Verified against production on
  // 2026-08-28: a hand-rolled probe carrying `?secret=...` reached the log line
  // intact before this was added.
  const t = redactCensusText(sanitizeLogField(record.t, MAX_VALUE_LENGTH))
  const v = redactCensusText(sanitizeLogField(record.v, MAX_VALUE_LENGTH))
  const f = stripQueryAndFragment(sanitizeLogField(record.f, MAX_FRAME_LENGTH))
  const p = stripQueryAndFragment(sanitizeLogField(record.p, MAX_PATH_LENGTH))

  // A line with neither a type nor a message names nothing; drop it rather than
  // writing an empty row that has to be filtered out of every later query.
  if (!t && !v) return null

  const rawOccurrence = record.n
  const n =
    typeof rawOccurrence === 'number' && Number.isInteger(rawOccurrence) && rawOccurrence > 0
      ? Math.min(rawOccurrence, Number.MAX_SAFE_INTEGER)
      : 1

  // Re-validated, not trusted: the class is narrow enough that `s` can never
  // carry a space, a newline or an `=`, so it cannot forge a field in the line.
  const rawSoundId = sanitizeLogField(record.s, 40)
  const s = SOUND_ID_PATTERN.test(rawSoundId) ? rawSoundId : ''

  return { t, v, f, p, n, s }
}

/**
 * The grep target. Read the accumulated census with:
 *   get_runtime_logs({ query: 'client-error census' })
 *
 * `v` is last because it is the only free-form field, so every fixed field
 * stays at a predictable position when the line is split.
 */
export function formatClientErrorCensusLine(census: ClientErrorCensus): string {
  return `client-error census n=${census.n} t=${census.t || '-'} p=${census.p || '-'} f=${census.f || '-'} s=${census.s || '-'} v=${census.v || '-'}`
}

/**
 * Reports one census line, if this event is due one.
 *
 * Must never throw and must never surface a failure: a census that can fail
 * loudly is a second telemetry pipe that can fail loudly, which is the shape
 * that caused this incident.
 */
export function recordClientErrorCensus(event: CensusEventLike): void {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return
    if (typeof location === 'undefined') return

    const payload = nextCensusPayload(event, location.pathname)
    if (!payload) return

    navigator.sendBeacon(
      CLIENT_ERROR_CENSUS_ENDPOINT,
      new Blob([JSON.stringify(payload)], { type: 'application/json' }),
    )
  } catch {
    // Deliberately silent. See property (1) in the module header.
  }
}
